import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { PerformanceSummary } from '@/hooks/usePartnerAnalytics';

interface RatingBreakdownDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractors: PerformanceSummary[];
}

export function RatingBreakdownDialog({ open, onOpenChange, contractors }: RatingBreakdownDialogProps) {
  // Filter out contractors without ratings FIRST
  const ratedContractors = contractors.filter(c => c.overall_rating !== null && c.overall_rating > 0);

  const ratingBands = [
    { range: '0-1', min: 0, max: 1, count: 0 },
    { range: '1-2', min: 1, max: 2, count: 0 },
    { range: '2-3', min: 2, max: 3, count: 0 },
    { range: '3-4', min: 3, max: 4, count: 0 },
    { range: '4-5', min: 4, max: 5, count: 0 },
  ];

  // Only process contractors with ratings
  ratedContractors.forEach(c => {
    const rating = c.overall_rating;
    const band = ratingBands.find(b => rating >= b.min && rating < b.max) || ratingBands[ratingBands.length - 1];
    band.count++;
  });

  const chartConfig = {
    count: {
      label: 'Contractors',
      color: 'hsl(var(--chart-1))',
    },
  };

  // Calculate stats only from rated contractors
  const total = ratedContractors.length;
  const mean = total > 0 
    ? ratedContractors.reduce((sum, c) => sum + c.overall_rating, 0) / total 
    : 0;
  const sorted = ratedContractors.map(c => c.overall_rating).sort((a, b) => a - b);
  const median = sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Rating Distribution</DialogTitle>
        </DialogHeader>

        {total === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <p className="text-lg">No rating data available</p>
            <p className="text-sm mt-2">Add contractor ratings to see distribution metrics</p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-3 mb-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{mean.toFixed(2)}</div>
                  <p className="text-xs text-muted-foreground">Mean Rating</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{median.toFixed(2)}</div>
                  <p className="text-xs text-muted-foreground">Median Rating</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{total}</div>
                  <p className="text-xs text-muted-foreground">Total Rated</p>
                </CardContent>
              </Card>
            </div>

            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ratingBands}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="range" 
                    label={{ value: 'Rating Range', position: 'insideBottom', offset: -5 }}
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <YAxis 
                    label={{ value: 'Number of Contractors', angle: -90, position: 'insideLeft' }}
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
